/**
 * Per-user activity drill-down at /it/telemetry/user?email=...
 *
 * Reached by clicking a name on the Telemetry page's "Activity by user"
 * report. Shows that person's full activity feed (every page view / event)
 * over a selectable time range, independent of the parent report's window.
 *
 * Back link top-left to /it/telemetry per .claude/rules/cockpit-ui-navigation.md.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { gridTheadClassName, SortableHeader, type SortDir } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { FilterLabel } from '@/components/FilterLabel';
import { ClearableSearchInput } from '@/components/ClearableSearchInput';
import { formatDateTime } from '@/lib/dateUtils';
import {
  columnLabel,
  downloadTelemetryXlsx,
  matchesSearch,
  sortTelemetryRows,
  useTelemetryDetail,
  RANGES,
  DEFAULT_HOURS,
} from '@/lib/useTelemetry';
import { TelemetryCards } from './Telemetry';

const COLUMNS = ['ts', 'site', 'path', 'event', 'sourceIp'];

function parseSortParam(raw: string | null): { column: string; dir: SortDir } | null {
  if (!raw) return null;
  const [col, dir] = raw.split(':');
  if (!col || !dir) return null;
  if (dir !== 'asc' && dir !== 'desc') return null;
  return { column: col, dir };
}

export function UserActivityDetail() {
  const [searchParams, setSearchParams] = useSearchParams();
  const email = searchParams.get('email') ?? '';
  const hoursParam = Number(searchParams.get('hours'));
  const hours = RANGES.some((r) => r.hours === hoursParam) ? hoursParam : DEFAULT_HOURS;
  const search = searchParams.get('q') ?? '';
  const sortParam = parseSortParam(searchParams.get('sort'));
  const sortBy = sortParam?.column ?? null;
  const sortDir: SortDir = sortParam?.dir ?? 'asc';

  const updateParams = (mutate: (sp: URLSearchParams) => void) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      },
      { replace: true },
    );
  };
  const setHours = (v: number) => updateParams((sp) => sp.set('hours', String(v)));
  const setSearch = (v: string) => updateParams((sp) => (v ? sp.set('q', v) : sp.delete('q')));
  const toggleSort = (col: string) =>
    updateParams((sp) => {
      if (sortBy !== col) sp.set('sort', `${col}:asc`);
      else if (sortDir === 'asc') sp.set('sort', `${col}:desc`);
      else sp.delete('sort');
    });

  const { data, isLoading, error } = useTelemetryDetail(
    'user_activity_detail',
    { email, hours },
    Boolean(email),
  );
  const rawRows = useMemo(() => data?.rows ?? [], [data]);
  const searchedRows = useMemo(
    () => rawRows.filter((r) => matchesSearch(r, COLUMNS, search)),
    [rawRows, search],
  );
  const rows = useMemo(
    () => sortTelemetryRows(searchedRows, sortBy, sortDir),
    [searchedRows, sortBy, sortDir],
  );

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const onExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadTelemetryXlsx('user_activity_detail', hours, { email });
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Shared loading / error / empty copy for the table body and the mobile cards.
  const notice: ReactNode | null =
    rows.length > 0 ? null : isLoading ? (
      <span className="inline-flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading activity...
      </span>
    ) : error ? (
      `Could not load activity: ${error.message}`
    ) : (
      'No activity in this window.'
    );

  if (!email) {
    return (
      <div className="h-full flex flex-col min-h-0">
        <Link
          to="/it/telemetry"
          className="text-sm text-amp-ink-muted hover:text-amp-ink inline-flex items-center gap-1 mb-3 dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
        >
          <ArrowLeft className="h-3 w-3" /> Back to Telemetry
        </Link>
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          No email address was given, so there is nothing to show here. Go back to Telemetry and click a name on
          Activity by user.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      <Link
        to="/it/telemetry"
        className="text-sm text-amp-ink-muted hover:text-amp-ink inline-flex items-center gap-1 mb-3 dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
      >
        <ArrowLeft className="h-3 w-3" /> Back to Telemetry
      </Link>
      <div className="mb-6">
        <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">
          IT - Telemetry
        </p>
        <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">{email}</h1>
        <p className="mt-1 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          Every page view and event this person triggered in the selected time range.
        </p>
      </div>

      {exportError ? (
        <div className="mb-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/40 dark:text-red-200">
          {exportError}
        </div>
      ) : null}

      <DataGridShell
        title="Activity"
        loaded={rows.length}
        countNoun="events"
        activeFilterCount={search ? 1 : 0}
        renderCards={() => (
          <TelemetryCards
            report="activity_by_user"
            columns={COLUMNS}
            rows={rows}
            notice={notice}
          />
        )}
        toolbar={
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <FilterLabel as="div" className="!mb-0">
                Time range
              </FilterLabel>
              <select
                value={String(hours)}
                onChange={(e) => setHours(Number(e.target.value))}
                className="rounded-md border border-amp-line bg-amp-white px-3 py-1.5 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
              >
                {RANGES.map((r) => (
                  <option key={r.hours} value={r.hours}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[200px] flex flex-col gap-1">
              <FilterLabel as="div" className="!mb-0">
                Search
              </FilterLabel>
              <ClearableSearchInput
                value={search}
                onChange={setSearch}
                placeholder="Filter loaded rows..."
                className="w-full border border-amp-line rounded pl-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
              />
            </div>
            <button
              type="button"
              onClick={() => void onExport()}
              disabled={exporting}
              className="inline-flex items-center gap-1.5 rounded-md border border-amp-line bg-amp-white px-3 py-1.5 text-sm text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm disabled:opacity-50"
            >
              {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              Export
            </button>
          </div>
        }
      >
        <table className="w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              {COLUMNS.map((c) => (
                <SortableHeader
                  key={c}
                  label={columnLabel(c)}
                  sorted={sortBy === c ? sortDir : null}
                  onToggle={() => toggleSort(c)}
                  thClassName="px-4 py-2"
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {notice ? (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  className="px-4 py-6 text-center text-amp-ink-muted dark:text-amp-dark-ink-muted"
                >
                  {notice}
                </td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <tr key={i} className="border-t border-amp-line dark:border-amp-dark-line">
                  <td className="px-4 py-2 whitespace-nowrap text-amp-ink dark:text-amp-dark-ink">
                    {formatDateTime(row.ts)}
                  </td>
                  <td className="px-4 py-2 text-amp-ink dark:text-amp-dark-ink">{row.site || '—'}</td>
                  <td className="px-4 py-2 break-all text-amp-ink dark:text-amp-dark-ink">{row.path || '—'}</td>
                  <td className="px-4 py-2 text-amp-ink dark:text-amp-dark-ink">{row.event || '—'}</td>
                  <td className="px-4 py-2 text-amp-ink dark:text-amp-dark-ink">{row.sourceIp || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DataGridShell>
    </div>
  );
}
